import React, { useState, useEffect, useRef, useCallback } from 'react';
import { notificationsAPI } from '../services/api';
import './NotificationSettings.css';

// Sections order for swipe navigation
const SECTIONS = ['global', 'types', 'proximity', 'privacy'] as const;
type SectionType = typeof SECTIONS[number];

interface GlobalSettings {
  notifications_enabled: boolean;
  do_not_disturb: boolean;
  dnd_start_hour: number;
  dnd_end_hour: number;
  email_digest: 'never' | 'daily' | 'weekly';
  sounds_enabled: boolean;
  badge_enabled: boolean;
  proximity_radius: number;
  proximity_cooldown: number;
  same_city_notifications: boolean;
  ghost_mode: boolean;
  hide_position: boolean;
  auto_delete_after_days: number | null;
}

interface NotificationPreference {
  notification_type: string;
  enabled: boolean;
  channels: string[];
}

const NOTIFICATION_TYPES = [
  { type: 'friend_request', label: 'Demandes d\'ami', icon: '👋', description: 'Quand quelqu\'un vous envoie une demande d\'ami' },
  { type: 'friend_accepted', label: 'Ami accepté', icon: '✅', description: 'Quand votre demande d\'ami est acceptée' },
  { type: 'friend_nearby', label: 'Ami à proximité', icon: '📌', description: 'Quand un ami crée une adresse temporaire proche de vous' },
  { type: 'friend_moved', label: 'Ami a déménagé', icon: '🗺️', description: 'Quand un ami change d\'adresse permanente vers une autre ville' },
  { type: 'friend_arrived_city', label: 'Ami débarque en ville', icon: '🚶', description: 'Quand un ami change d\'adresse permanente dans votre ville ou crée une adresse temporaire dans votre ville' },
  { type: 'temp_location_shared', label: 'Position temporaire partagée', icon: '📍', description: 'Quand un ami partage une position temporaire' },
  { type: 'temp_location_expiring', label: 'Position bientôt expirée', icon: '⏰', description: 'Quand votre position temporaire va expirer (10% avant la fin)' },
  { type: 'all_temp_locations_inactive', label: 'Aucune position active', icon: '🚫', description: 'Quand toutes vos positions sont inactives' },
  { type: 'milestone_friends', label: 'Palier d\'amis atteint', icon: '🌟', description: 'Quand vous atteignez un palier d\'amis (1, 5, 20, 50, 100...)' },
  { type: 'milestone_temp_locations', label: 'Palier de positions atteint', icon: '🎯', description: 'Quand vous atteignez un palier de positions (1, 5, 20, 50, 100...)' },
  { type: 'account_inactive', label: 'Compte inactif', icon: '⚠️', description: 'Rappel si votre compte est inactif (à 15 et 30 jours)' },
  { type: 'contact_joined', label: 'Contact a rejoint', icon: '📱', description: 'Quand un de vos contacts téléphoniques rejoint l\'application' },
];

const NotificationSettings: React.FC = () => {
  const [globalSettings, setGlobalSettings] = useState<GlobalSettings | null>(null);
  const [preferences, setPreferences] = useState<Map<string, NotificationPreference>>(new Map());
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<SectionType>('global');
  const [saveMessage, setSaveMessage] = useState<string>('');

  // Swipe handling
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const touchEndY = useRef<number>(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const minSwipeDistance = 30; // Reduced for easier swipe

  useEffect(() => {
    loadSettings();
  }, []);

  // Auto-scroll tabs to keep active section visible
  useEffect(() => {
    if (tabsRef.current) {
      const activeButton = tabsRef.current.querySelector('button.active') as HTMLElement;
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [activeSection]);

  const handleSwipe = useCallback(() => {
    const distanceX = touchStartX.current - touchEndX.current;
    const distanceY = touchStartY.current - touchEndY.current;

    // Only swipe if horizontal movement > vertical movement (avoid conflict with scroll)
    if (Math.abs(distanceX) < Math.abs(distanceY)) return;

    const isLeftSwipe = distanceX > minSwipeDistance;
    const isRightSwipe = distanceX < -minSwipeDistance;

    const currentIndex = SECTIONS.indexOf(activeSection);

    if (isLeftSwipe && currentIndex < SECTIONS.length - 1) {
      setActiveSection(SECTIONS[currentIndex + 1]);
    } else if (isRightSwipe && currentIndex > 0) {
      setActiveSection(SECTIONS[currentIndex - 1]);
    }
  }, [activeSection]);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
    touchStartY.current = e.targetTouches[0].clientY;
    touchEndX.current = e.targetTouches[0].clientX;
    touchEndY.current = e.targetTouches[0].clientY;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
    touchEndY.current = e.targetTouches[0].clientY;
  }, []);

  const onTouchEnd = useCallback(() => {
    handleSwipe();
  }, [handleSwipe]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [settingsRes, preferencesRes] = await Promise.all([
        notificationsAPI.getGlobalSettings(),
        notificationsAPI.getPreferences(),
      ]);

      setGlobalSettings(settingsRes.settings);

      const prefsMap = new Map<string, NotificationPreference>();
      preferencesRes.preferences.forEach((pref: NotificationPreference) => {
        prefsMap.set(pref.notification_type, pref);
      });
      setPreferences(prefsMap);
    } catch (error) {
      console.error('Failed to load notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateGlobalSetting = async (key: keyof GlobalSettings, value: any) => {
    if (!globalSettings) return;

    try {
      const updated = { ...globalSettings, [key]: value };
      setGlobalSettings(updated);

      await notificationsAPI.updateGlobalSettings({ [key]: value });
      showSaveMessage('Paramètres enregistrés');
    } catch (error) {
      console.error('Failed to update global setting:', error);
      showSaveMessage('Erreur lors de la sauvegarde', true);
    }
  };

  const updatePreference = async (type: string, updates: Partial<NotificationPreference>) => {
    const current = preferences.get(type) || {
      notification_type: type,
      enabled: true,
      channels: ['in_app'],
    };

    const updated = { ...current, ...updates };

    try {
      setPreferences(new Map(preferences.set(type, updated)));

      await notificationsAPI.upsertPreference(type, {
        enabled: updated.enabled,
        channels: updated.channels,
      });

      showSaveMessage('Préférence enregistrée');
    } catch (error) {
      console.error('Failed to update preference:', error);
      showSaveMessage('Erreur lors de la sauvegarde', true);
    }
  };

  const showSaveMessage = (message: string, isError: boolean = false) => {
    setSaveMessage(message);
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const getPreference = (type: string): NotificationPreference => {
    return preferences.get(type) || {
      notification_type: type,
      enabled: true,
      channels: ['in_app'],
    };
  };

  if (loading || !globalSettings) {
    return <div className="notification-settings-loading">Chargement des paramètres...</div>;
  }

  return (
    <div className="notification-settings">
      {saveMessage && (
        <div className={`save-message ${saveMessage.includes('Erreur') ? 'error' : 'success'}`}>
          {saveMessage}
        </div>
      )}

      {/* Navigation tabs */}
      <div className="notification-settings-tabs" ref={tabsRef}>
        <button
          className={activeSection === 'global' ? 'active' : ''}
          onClick={() => setActiveSection('global')}
        >
          🌍 Général
        </button>
        <button
          className={activeSection === 'types' ? 'active' : ''}
          onClick={() => setActiveSection('types')}
        >
          📝 Par type
        </button>
        <button
          className={activeSection === 'proximity' ? 'active' : ''}
          onClick={() => setActiveSection('proximity')}
        >
          📍 Proximité
        </button>
        <button
          className={activeSection === 'privacy' ? 'active' : ''}
          onClick={() => setActiveSection('privacy')}
        >
          🔒 Confidentialité
        </button>
      </div>

      {/* Swipeable content area */}
      <div
        className="notification-settings-content"
        ref={contentRef}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Global Settings */}
        {activeSection === 'global' && (
          <div className="settings-section">
            <h3>Réglages globaux</h3>
          <p className="section-description">Contrôlez l'activation globale des notifications</p>

          <div className="setting-item">
            <div className="setting-info">
              <label>Activer les notifications</label>
              <p>Activer ou désactiver toutes les notifications</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={globalSettings.notifications_enabled}
                onChange={(e) => updateGlobalSetting('notifications_enabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Mode Ne pas déranger</label>
              <p>Suspendre les notifications pendant certaines heures</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={globalSettings.do_not_disturb}
                onChange={(e) => updateGlobalSetting('do_not_disturb', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          {globalSettings.do_not_disturb && (
            <div className="setting-item-inline">
              <label>Heures de silence :</label>
              <select
                value={globalSettings.dnd_start_hour}
                onChange={(e) => updateGlobalSetting('dnd_start_hour', parseInt(e.target.value))}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{i}h00</option>
                ))}
              </select>
              <span>à</span>
              <select
                value={globalSettings.dnd_end_hour}
                onChange={(e) => updateGlobalSetting('dnd_end_hour', parseInt(e.target.value))}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{i}h00</option>
                ))}
              </select>
            </div>
          )}

          <div className="setting-item">
            <div className="setting-info">
              <label>Digest email</label>
              <p>Recevoir un récapitulatif par email</p>
            </div>
            <select
              value={globalSettings.email_digest}
              onChange={(e) => updateGlobalSetting('email_digest', e.target.value)}
              className="setting-select"
            >
              <option value="never">Jamais</option>
              <option value="daily">Quotidien</option>
              <option value="weekly">Hebdomadaire</option>
            </select>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Sons</label>
              <p>Activer les sons de notification</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={globalSettings.sounds_enabled}
                onChange={(e) => updateGlobalSetting('sounds_enabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Badge de notification</label>
              <p>Afficher le compteur sur l'icône</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={globalSettings.badge_enabled}
                onChange={(e) => updateGlobalSetting('badge_enabled', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Suppression automatique</label>
              <p>Supprimer les notifications après un certain temps</p>
            </div>
            <select
              value={globalSettings.auto_delete_after_days || ''}
              onChange={(e) => updateGlobalSetting('auto_delete_after_days', e.target.value ? parseInt(e.target.value) : null)}
              className="setting-select"
            >
              <option value="">Jamais</option>
              <option value="7">7 jours</option>
              <option value="30">30 jours</option>
              <option value="90">90 jours</option>
            </select>
          </div>
        </div>
      )}

      {/* Notification Types Settings */}
      {activeSection === 'types' && (
        <div className="settings-section">
          <h3>Paramètres par type de notification</h3>
          <p className="section-description">Personnalisez chaque type de notification individuellement</p>

          {NOTIFICATION_TYPES.map((notifType) => {
            const pref = getPreference(notifType.type);

            return (
              <div key={notifType.type} className="notification-type-setting">
                <div className="notification-type-header">
                  <div className="notification-type-info">
                    <span className="notification-type-icon">{notifType.icon}</span>
                    <div>
                      <h4>{notifType.label}</h4>
                      <p>{notifType.description}</p>
                    </div>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={pref.enabled}
                      onChange={(e) => updatePreference(notifType.type, { enabled: e.target.checked })}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                {/* Show proximity radius slider for friend_nearby notification */}
                {notifType.type === 'friend_nearby' && pref.enabled && (
                  <div className="notification-type-details">
                    <div className="proximity-slider-inline">
                      <label>Rayon de proximité : {(globalSettings.proximity_radius / 1000).toFixed(1)} km</label>
                      <input
                        type="range"
                        min="100"
                        max="10000"
                        step="100"
                        value={globalSettings.proximity_radius}
                        onChange={(e) => updateGlobalSetting('proximity_radius', parseInt(e.target.value))}
                        className="setting-slider"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Proximity Settings */}
      {activeSection === 'proximity' && (
        <div className="settings-section">
          <h3>Paramètres de proximité</h3>
          <p className="section-description">Configurez les notifications de géolocalisation</p>

          <div className="setting-item">
            <div className="setting-info">
              <label>Rayon de proximité</label>
              <p>Distance pour considérer un ami comme "à proximité" : {(globalSettings.proximity_radius / 1000).toFixed(1)} km</p>
            </div>
            <input
              type="range"
              min="100"
              max="10000"
              step="100"
              value={globalSettings.proximity_radius}
              onChange={(e) => updateGlobalSetting('proximity_radius', parseInt(e.target.value))}
              className="setting-slider"
            />
          </div>

          <div className="proximity-info-box">
            <h4>ℹ️ Gestion intelligente des notifications</h4>
            <p>Les notifications de proximité sont automatiquement optimisées :</p>
            <ul>
              <li><strong>Passage à proximité d'un ami</strong> : Une notification par ami et par jour maximum</li>
              <li><strong>Création d'une adresse temporaire</strong> : Notification 5 minutes après création seulement</li>
              <li><strong>Déménagements</strong> : Maximum une notification par jour</li>
            </ul>
            <p className="info-note">Ces règles intelligentes évitent le spam tout en vous tenant informé au bon moment.</p>
          </div>
        </div>
      )}

      {/* Privacy Settings */}
      {activeSection === 'privacy' && (
        <div className="settings-section">
          <h3>Paramètres de confidentialité</h3>
          <p className="section-description">Contrôlez les notifications que vous envoyez aux autres</p>

          <div className="setting-item">
            <div className="setting-info">
              <label>Mode fantôme</label>
              <p>Ne pas envoyer de notifications sur vos actions aux autres utilisateurs</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={globalSettings.ghost_mode}
                onChange={(e) => updateGlobalSetting('ghost_mode', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-info">
              <label>Masquer ma position</label>
              <p>Ne pas envoyer de notifications de proximité vous concernant</p>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={globalSettings.hide_position}
                onChange={(e) => updateGlobalSetting('hide_position', e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="privacy-notice">
            <strong>ℹ️ Note importante :</strong>
            <p>Le mode fantôme et le masquage de position n'affectent que les notifications automatiques. Vos amis peuvent toujours voir votre position sur la carte si vous avez partagé vos informations.</p>
          </div>
        </div>
      )}
      </div>{/* End swipeable content */}
    </div>
  );
};

export default NotificationSettings;
